import { z } from 'zod';
import { FieldIdSchema, FORM_LIMITS } from './primitives.js';
/**
 * Conditional visibility (FR-INTAKE-01). A condition is either one comparison against
 * another field's answer, or an `all` / `any` group of conditions.
 *
 * The schema is built as three explicit levels rather than with `z.lazy`, so nesting is
 * bounded *by construction*: a hostile definition cannot recurse the parser, and the
 * builder can rely on a depth the renderer will always be able to evaluate.
 */
export const CONDITION_OPS = [
    'eq',
    'neq',
    'in',
    'notIn',
    'isEmpty',
    'notEmpty',
    'gt',
    'lt',
];
/** Operators that take no `value`. */
const NULLARY_OPS = new Set(['isEmpty', 'notEmpty']);
/** Operators whose `value` is a list. */
const LIST_OPS = new Set(['in', 'notIn']);
/** Operators that compare numerically or lexicographically. */
const ORDERED_OPS = new Set(['gt', 'lt']);
const ScalarSchema = z.union([z.string().max(200), z.number(), z.boolean()]);
const LeafSchema = z
    .object({
    field: FieldIdSchema,
    op: z.enum(CONDITION_OPS),
    value: z
        .union([ScalarSchema, z.array(ScalarSchema).min(1).max(FORM_LIMITS.maxOptions)])
        .optional(),
})
    .superRefine((leaf, ctx) => {
    const hasValue = leaf.value !== undefined;
    if (NULLARY_OPS.has(leaf.op)) {
        if (hasValue) {
            ctx.addIssue({ code: 'custom', path: ['value'], message: `"${leaf.op}" takes no value.` });
        }
        return;
    }
    if (!hasValue) {
        ctx.addIssue({ code: 'custom', path: ['value'], message: `"${leaf.op}" needs a value.` });
        return;
    }
    if (LIST_OPS.has(leaf.op) !== Array.isArray(leaf.value)) {
        ctx.addIssue({
            code: 'custom',
            path: ['value'],
            message: LIST_OPS.has(leaf.op)
                ? `"${leaf.op}" needs a list of values.`
                : `"${leaf.op}" needs a single value.`,
        });
    }
    if (ORDERED_OPS.has(leaf.op) && typeof leaf.value === 'boolean') {
        ctx.addIssue({
            code: 'custom',
            path: ['value'],
            message: `"${leaf.op}" cannot compare a yes/no answer.`,
        });
    }
});
function group(inner) {
    const children = z.array(inner).min(1).max(FORM_LIMITS.maxConditionChildren);
    return z.union([z.object({ all: children }), z.object({ any: children })]);
}
const Level1 = LeafSchema;
const Level2 = z.union([LeafSchema, group(Level1)]);
const Level3 = z.union([LeafSchema, group(Level2)]);
/**
 * A condition nested at most `FORM_LIMITS.maxConditionDepth` levels deep
 * (a leaf is depth 1, a group of leaves is depth 2).
 */
export const ConditionSchema = Level3;
/** True when the value is a group rather than a comparison. */
export function isConditionGroup(condition) {
    return 'all' in condition || 'any' in condition;
}
/** Depth of an already-parsed condition. Exported for the builder's guard rails (M8.5). */
export function conditionDepth(condition) {
    if (!isConditionGroup(condition))
        return 1;
    const children = 'all' in condition ? condition.all : condition.any;
    return 1 + Math.max(...children.map(conditionDepth));
}
/** Every field id a condition reads, in document order, de-duplicated. */
export function conditionFieldIds(condition) {
    const seen = [];
    const walk = (node) => {
        if (!isConditionGroup(node)) {
            if (!seen.includes(node.field))
                seen.push(node.field);
            return;
        }
        for (const child of 'all' in node ? node.all : node.any)
            walk(child);
    };
    walk(condition);
    return seen;
}
/** An answer counts as empty when it is missing, blank, or an empty list. */
export function isEmptyAnswer(value) {
    if (value === undefined || value === null)
        return true;
    if (typeof value === 'string')
        return value.trim() === '';
    if (Array.isArray(value))
        return value.length === 0;
    return false;
}
function compare(left, right) {
    if (typeof left === 'number' && typeof right === 'number')
        return left - right;
    if (typeof left === 'string' && typeof right === 'string')
        return left.localeCompare(right);
    if (typeof left === 'string' && typeof right === 'number') {
        const parsed = Number(left);
        return Number.isFinite(parsed) ? parsed - right : null;
    }
    return null;
}
function sameValue(left, right) {
    if (Array.isArray(left))
        return left.some((item) => sameValue(item, right));
    if (typeof left === 'boolean' || typeof right === 'boolean')
        return left === right;
    return String(left) === String(right);
}
function evaluateLeaf(leaf, read) {
    const value = read(leaf.field);
    switch (leaf.op) {
        case 'isEmpty':
            return isEmptyAnswer(value);
        case 'notEmpty':
            return !isEmptyAnswer(value);
        case 'eq':
            return !Array.isArray(leaf.value) && leaf.value !== undefined && sameValue(value, leaf.value);
        case 'neq':
            return (!Array.isArray(leaf.value) && leaf.value !== undefined && !sameValue(value, leaf.value));
        case 'in':
            return (Array.isArray(leaf.value) && leaf.value.some((candidate) => sameValue(value, candidate)));
        case 'notIn':
            return (Array.isArray(leaf.value) && !leaf.value.some((candidate) => sameValue(value, candidate)));
        case 'gt': {
            if (Array.isArray(leaf.value) || leaf.value === undefined)
                return false;
            const delta = compare(value, leaf.value);
            return delta !== null && delta > 0;
        }
        case 'lt': {
            if (Array.isArray(leaf.value) || leaf.value === undefined)
                return false;
            const delta = compare(value, leaf.value);
            return delta !== null && delta < 0;
        }
        default:
            return false;
    }
}
/**
 * Evaluates a condition. `read` returns the *effective* answer for a field - callers pass
 * a reader that reports hidden fields as empty, so a question buried under a closed branch
 * can never keep another branch open.
 */
export function evaluateCondition(condition, read) {
    if (!isConditionGroup(condition))
        return evaluateLeaf(condition, read);
    if ('all' in condition)
        return condition.all.every((child) => evaluateCondition(child, read));
    return condition.any.some((child) => evaluateCondition(child, read));
}
//# sourceMappingURL=condition.js.map